import React, { useState } from "react";
import { CircularProgress, Typography, Box, useMediaQuery, useTheme } from "@mui/material";
import ImageListModal from "../../../CommonComponents/ImageListModal/ImageListModal";
import { RevisionAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/RevisionAPIHandler";
import { useSignedResource } from "../../../../hooks/useSignedResource";

interface RevisionRequestCardProps {
    revisionId: string;
}

const fetchRevision = (revisionId: string) => RevisionAPIHandler.shared.getRevisionById(revisionId);

const RevisionRequestCard = ({ revisionId }: RevisionRequestCardProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Attachment URLs are short-lived signed URLs, so they are re-signed when the user
    // comes back to a tab that was left open.
    const { data: revision, loading } = useSignedResource(revisionId, fetchRevision);
    const [openModal, setOpenModal] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const getResponsiveStyles = () => ({
        reviewRequestMessage: { 
            width: '100%', 
            minWidth: isMobile ? '100%' : '500px', 
            padding: isMobile ? '1rem' : '2rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: 'rgb(255, 200, 173)', 
            margin: isMobile ? '5px 0' : '10px 0' 
        },
        reviewRequestHeadline: { 
            fontWeight: 'bold', 
            fontSize: isMobile ? '1.2rem' : '1.5rem' 
        },
        reviewRequestContent: { 
            padding: isMobile ? '0.75rem' : '1rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: 'white', 
            margin: isMobile ? '0.75rem 0rem' : '1rem 0rem', 
            lineHeight: '1.7rem', 
            fontSize: isMobile ? '0.8rem' : '0.9rem' 
        },
        reviewRequestAttachments: { 
            display: 'flex' as const, 
            flexDirection: 'column' as const, 
            padding: isMobile ? '0.75rem' : '1rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: 'white', 
            margin: isMobile ? '0.75rem 0rem' : '1rem 0rem', 
            overflowX: 'auto' as const, 
            overflowY: 'hidden' as const, 
            whiteSpace: 'nowrap' as const, 
            maxWidth: '100%' 
        },
        revisionAttachmentBox: { 
            width: isMobile ? '4rem' : '5rem', 
            height: isMobile ? '4rem' : '5rem', 
            margin: isMobile ? '8px 0px' : '10px 0px', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: '#D7D7D7', 
            overflow: 'hidden' as const, 
            display: 'flex' as const, 
            alignItems: 'center' as const, 
            justifyContent: 'center' as const, 
            cursor: 'pointer' as const
        },
        revisionAttachmentImage: { 
            width: '100%', 
            height: "auto" 
        },
        requestDescBlock: { 
            padding: isMobile ? '0.75rem' : '1rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: '#FFFFFF', 
            marginTop: isMobile ? '0.75rem' : '1rem' 
        },
    });

    const revisionStyle = getResponsiveStyles();

    const handleAttachmentClick = (idx: number) => {
        setCurrentIndex(idx);
        setOpenModal(true);
    };

    if (loading) {
        return (
            <div
                className="revisionRequestMessage"
                style={{
                    ...revisionStyle.reviewRequestMessage,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minHeight: 370,
                    justifyContent: "center"
                }}
            >
                <CircularProgress />
                <Box
                    sx={{
                        width: "100%",
                        height: 120,
                        background: "#f3f3f3",
                        borderRadius: "10px",
                        marginTop: 3
                    }}
                />
            </div>
        );
    }

    if (!revision) {
        return (
            <div style={{ padding: "2rem" }}>
                <Typography color="error">Failed to load revision request.</Typography>
            </div>
        );
    }

    const requestAttachments = Array.isArray(revision.requestAttachments)
        ? revision.requestAttachments
        : [];

    return (
        <div className="revisionRequestMessage" style={revisionStyle.reviewRequestMessage}>
            <div className="revisionRequestHeadline" style={revisionStyle.reviewRequestHeadline}>Revision Request</div>
            <div className='requestDesc-block' style={revisionStyle.requestDescBlock}>
                <Typography className='requestDesc-title' fontWeight={700}>Requested Comments</Typography>
                <div className="requestDesc-content">
                    {revision.requestDesc ?? "No requestDesc added for this revision request"}
                </div>
            </div>
            {requestAttachments.length > 0 &&
                <div className="revisionRequestAttachments" style={revisionStyle.reviewRequestAttachments}>
                    <Typography className='revisionRequestAttachments-title' fontWeight={700}>Attachments</Typography>
                    {
                    requestAttachments.map((item: string, idx: number) => (
                        <div
                            className='revisionAttachmentBox'
                            style={revisionStyle.revisionAttachmentBox}
                            key={idx}
                            onClick={() => handleAttachmentClick(idx)}
                        >
                            <img src={item} className='revisionAttachmentImage' style={revisionStyle.revisionAttachmentImage} alt='attachments' />
                        </div>
                    ))}
                </div>
            }
            <ImageListModal
                images={requestAttachments}
                open={openModal}
                initialIndex={currentIndex}
                onClose={() => setOpenModal(false)}
            />
        </div>
    );
};

export default RevisionRequestCard;